import React, { Component } from 'react';
import _ from 'lodash';
import * as Sentry from '@sentry/browser';
import classNames from 'classnames';
import actions from 'api/actions';
import LearningCenter from 'components/shared/landing/learning-center';
import PlanCard from 'components/shared/plan-card';
import Input from 'components/shared/input';
import Select from 'components/shared/select';
import styles from './styles.module.scss';
import { escapeStringRegexp } from 'utils/escape-string-regexp';

export default class Marketplace extends Component {
  static defaultProps = {
    defaultType: 'services',
    learningCenters: [],
    services: [],
    serviceDetailsButtonType: 'modal',
    centerLinkPrefix: '/unyted-learning-centers',
  };

  constructor(props) {
    super(props);

    const { defaultType, learningCenters, services } = this.props;

    this.state = {
      searchFilter: '',
      focusFilter: '',
      locationFilter: '',
      interestFilter: '',
      learningCenters,
      services,
      typeSelected: defaultType,
    };

    this.onSearchChange = this.onSearchChange.bind(this);
    this.onFocusChange = this.onFocusChange.bind(this);
    this.onLocationChange = this.onLocationChange.bind(this);
    this.onInterestChange = this.onInterestChange.bind(this);
    this.onResetSearch = this.onResetSearch.bind(this);
    this.onSelectCenters = this.onSelectType.bind(this, 'centers');
    this.onSelectServices = this.onSelectType.bind(this, 'services');
  }

  // TODO: We're probably better off extracting these into session-durable
  // storage. Otherwise, this'll end up making a lot of requests for a parent
  // who's just reasonably browsing through our offerings
  componentDidMount() {
    const { learningCenters, services } = this.state;

    if (learningCenters.length === 0) {
      actions
        .get('learning_centers')
        .then(r => this.setState({ learningCenters: r.data }))
        .catch(e => Sentry.captureException(e));
    }

    if (services.length === 0) {
      actions
        .get('plans')
        .then(r => this.setState({ services: r.data }))
        .catch(e => Sentry.captureException(e));
    }
  }

  onSearchChange(e) {
    this.setState({ searchFilter: escapeStringRegexp(e.target.value) });
  }

  onFocusChange(e) {
    this.setState({ focusFilter: e.target.value });
  }

  onLocationChange(e) {
    this.setState({ locationFilter: e.target.value });
  }

  onInterestChange(e) {
    this.setState({ interestFilter: e.target.value });
  }

  onResetSearch() {
    this.setState({
      searchFilter: '',
      focusFilter: '',
      locationFilter: '',
      interestFilter: '',
    });
  }

  onSelectType(typeSelected) {
    this.setState({ typeSelected }, this.onResetSearch);
  }

  render() {
    let focuses, filteredCenters, filteredServices;
    const {
      searchFilter,
      focusFilter,
      locationFilter,
      interestFilter,
      learningCenters,
      services,
      typeSelected,
    } = this.state;
    const { centerLinkPrefix, serviceDetailsButtonType, serviceLinkPrefix } =
      this.props;
    const search = RegExp(searchFilter, 'i');
    if (typeSelected === 'centers') {
      filteredCenters = _.chain(learningCenters)
        .filter(
          center =>
            search.test(center.description) ||
            search.test(center.title) ||
            search.test(center.location) ||
            search.test(center.interest) ||
            _.some(center.subjects, subject => search.test(subject)),
        )
        .filter(
          center =>
            focusFilter === '' || _.includes(center.subjects, focusFilter),
        )
        .filter(
          center => locationFilter === '' || center.location === locationFilter,
        )
        .filter(
          center => interestFilter === '' || center.interest === interestFilter,
        )
        .value();

      focuses = _.uniq(_.flatMap(learningCenters, 'subjects')).sort();
    }
    if (typeSelected === 'services') {
      filteredServices = _.chain(services)
        .filter(
          service =>
            search.test(service.title) ||
            search.test(service.instructor_intro) ||
            search.test(service.description) ||
            search.test(service.target_audience) ||
            _.some(service.benefits, benefit => search.test(benefit)),
        )
        .filter(
          service =>
            focusFilter === '' || _.includes(service.benefits, focusFilter),
        )
        .value();

      focuses = _.uniq(_.flatMap(services, 'benefits')).sort();
    }

    const locations = _.uniq(_.map(learningCenters, 'location')).sort();

    return (
      <div className={styles.marketplaceIndex}>
        <div className={styles.switchButtonContainer}>
          <div className="row">
            <div className="col-lg-8 offset-lg-2 col-12 d-flex justify-content-center mb-3">
              <button
                type="button"
                onClick={this.onSelectServices}
                className={classNames({
                  [styles.selectTypeButton]: true,
                  [styles.noRightRadius]: true,
                  [styles.selected]: typeSelected === 'services',
                })}
              >
                Lessons
              </button>
              <button
                type="button"
                onClick={this.onSelectCenters}
                className={classNames({
                  [styles.selectTypeButton]: true,
                  [styles.noLeftRadius]: true,
                  [styles.selected]: typeSelected === 'centers',
                })}
              >
                Educators
              </button>
            </div>
          </div>
        </div>
        {typeSelected === 'centers' && (
          <section
            id="learning-centers-list"
            className={styles.learningCenters}
          >
            <div className={styles.controls}>
              <div className="row">
                <div className="col-lg-4 col-12 mb-3">
                  <Input
                    value={searchFilter}
                    onChange={this.onSearchChange}
                    placeholder="Search..."
                    icon="search"
                  />
                </div>
                <div className="col-lg-4 col-md-6 col-12 mb-3">
                  <Select
                    value={focusFilter}
                    onChange={this.onFocusChange}
                    white
                  >
                    <option value="">Filter by Subject</option>
                    {focuses.map(focus => (
                      <option key={focus} value={focus}>
                        {focus}
                      </option>
                    ))}
                  </Select>
                </div>
                <div className="col-lg-4 col-md-6 col-12">
                  <Select
                    value={locationFilter}
                    onChange={this.onLocationChange}
                    white
                  >
                    <option value="">Filter by Location</option>
                    {locations.map(location => (
                      <option key={location} value={location}>
                        {location}
                      </option>
                    ))}
                  </Select>
                </div>
              </div>
            </div>
            <div className="row">
              {filteredCenters.length > 0 ? (
                filteredCenters.map(center => (
                  <div key={center.id} className="col-12 mb-3">
                    <LearningCenter
                      id={center.id}
                      center={center}
                      name={center.instructor_name}
                      credentials={center.instructor_credentials}
                      title={center.title}
                      leaderPicture={center.image_file_name}
                      professionalTitle={center.professionalTitle}
                      description={center.description}
                      location={center.location}
                      subjects={center.subjects}
                      capacityMessage={center.capacityMessage}
                      linkPrefix={centerLinkPrefix}
                    />
                  </div>
                ))
              ) : (
                <div className="col-12 d-flex flex-row mb-3 justify-content-center align-items-center p-5">
                  <div className="d-flex flex-column justify-content-center align-items-center p-5">
                    <h3 className="font-heavy mb-5">
                      No Learning Centers match your search criteria. Please try
                      again!
                    </h3>
                    <button
                      type="button"
                      className="blue font-2x font-heavy"
                      onClick={this.onResetSearch}
                    >
                      Reset filters
                    </button>
                  </div>
                </div>
              )}
            </div>
          </section>
        )}
        {typeSelected === 'services' && (
          <section id="learning-services-list" className={styles.services}>
            <div className={styles.controls}>
              <div className="row">
                <div className="col-lg-6 col-12 mb-3">
                  <Input
                    value={searchFilter}
                    onChange={this.onSearchChange}
                    placeholder="Search..."
                    icon="search"
                  />
                </div>
                <div className="col-lg-6 col-12 mb-3">
                  <Select
                    value={focusFilter}
                    onChange={this.onFocusChange}
                    white
                  >
                    <option value="">Filter by Benefit</option>
                    {focuses.map(focus => (
                      <option key={focus} value={focus}>
                        {focus}
                      </option>
                    ))}
                  </Select>
                </div>
              </div>
            </div>
            <div className="row">
              <div className="d-flex flex-row flex-wrap justify-content-around">
                {filteredServices.length > 0 ? (
                  filteredServices.map(service => (
                    <PlanCard
                      key={service.id}
                      plan={service}
                      detailsButtonType={serviceDetailsButtonType}
                      linkPrefix={serviceLinkPrefix}
                    />
                  ))
                ) : (
                  <div className="d-flex flex-row mb-3 justify-content-center align-items-center p-5">
                    <div className="d-flex flex-column justify-content-center align-items-center p-5">
                      <h3 className="font-heavy mb-5">
                        No Learning Services match your search criteria. Please
                        try again!
                      </h3>
                      <button
                        type="button"
                        className="blue font-2x font-heavy"
                        onClick={this.onResetSearch}
                      >
                        Reset filters
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </section>
        )}
      </div>
    );
  }
}
